/**
 * Team Aliases API module
 * Handles CRUD operations for team name aliases
 * Used in tournament schedule import to resolve team name mismatches
 */

import { supabase } from '../lib/supabase';
import { createAPIMethod } from './base';
import { log } from '../utils/logger';

/**
 * Team Alias API namespace
 */
export const teamAliasAPI = {
  /**
   * Create a new team alias
   * @param {string} alias - The alternate name/abbreviation for the team
   * @param {string} teamId - The ID of the canonical team
   * @param {string} [createdBy] - User ID of who created the alias (optional, uses current user)
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    async (alias, teamId, createdBy = null) => {
      if (!alias || !teamId) {
        throw new Error('Alias and teamId are required');
      }

      const trimmedAlias = alias.trim();
      if (!trimmedAlias) {
        throw new Error('Alias cannot be empty');
      }

      const insertData = {
        alias: trimmedAlias,
        team_id: teamId
      };

      if (createdBy) {
        insertData.created_by = createdBy;
      }

      return await supabase
        .from('team_aliases')
        .insert(insertData)
        .select(`
          id,
          alias,
          team_id,
          created_at,
          created_by,
          teams (
            id,
            name
          )
        `)
        .single();
    },
    { operation: 'Create team alias' }
  ),

  /**
   * Create a team alias if it doesn't already exist (silently ignores duplicates)
   * @param {string} alias - The alternate name/abbreviation for the team
   * @param {string} teamId - The ID of the canonical team
   * @param {string} [createdBy] - User ID of who created the alias (optional)
   * @returns {Promise<{success: boolean, created: boolean, existing: boolean}>}
   */
  createIfNotExists: async (alias, teamId, createdBy = null) => {
    if (!alias || !teamId) {
      return { success: false, created: false, existing: false, error: 'Alias and teamId are required' };
    }

    const trimmedAlias = alias.trim();
    if (!trimmedAlias) {
      return { success: false, created: false, existing: false, error: 'Alias cannot be empty' };
    }

    try {
      // First check if it already exists
      const { data: existing } = await supabase
        .from('team_aliases')
        .select('id, team_id')
        .ilike('alias', trimmedAlias)
        .limit(1)
        .single();

      if (existing) {
        // Alias already exists - this is fine, not an error
        return { success: true, created: false, existing: true, aliasId: existing.id };
      }

      // Doesn't exist, try to create
      const insertData = {
        alias: trimmedAlias,
        team_id: teamId
      };

      if (createdBy) {
        insertData.created_by = createdBy;
      }

      const { data, error } = await supabase
        .from('team_aliases')
        .insert(insertData)
        .select('id')
        .single();

      if (error) {
        // Check if it's a duplicate key error (race condition)
        if (error.code === '23505') {
          return { success: true, created: false, existing: true };
        }
        return { success: false, created: false, existing: false, error: error.message };
      }

      return { success: true, created: true, existing: false, aliasId: data.id };
    } catch (err) {
      return { success: false, created: false, existing: false, error: err.message };
    }
  },

  /**
   * Get team by alias (case-insensitive lookup)
   * @param {string} alias - The alias to look up
   * @returns {Promise<APIResponse>} - Returns the team data if found
   */
  getByAlias: createAPIMethod(
    async (alias) => {
      if (!alias) {
        return { data: null, error: null };
      }

      const trimmedAlias = alias.trim().toLowerCase();

      const { data, error } = await supabase
        .from('team_aliases')
        .select(`
          id,
          alias,
          team_id,
          teams (
            id,
            name,
            state,
            division,
            sport_activity,
            is_active
          )
        `)
        .ilike('alias', trimmedAlias)
        .limit(1)
        .single();

      if (error && error.code === 'PGRST116') {
        // No rows returned - not an error, just no match
        return { data: null, error: null };
      }

      if (error) {
        throw error;
      }

      return { data, error: null };
    },
    { operation: 'Get team by alias' }
  ),

  /**
   * Get all aliases for a specific team
   * @param {string} teamId - The team ID
   * @returns {Promise<APIResponse>}
   */
  getByTeamId: createAPIMethod(
    async (teamId) => {
      if (!teamId) {
        return { data: [], error: null };
      }

      return await supabase
        .from('team_aliases')
        .select(`
          id,
          alias,
          team_id,
          created_at,
          created_by
        `)
        .eq('team_id', teamId)
        .order('alias', { ascending: true });
    },
    { operation: 'Get aliases by team' }
  ),

  /**
   * Get all team aliases (for building lookup cache)
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async () => {
      return await supabase
        .from('team_aliases')
        .select(`
          id,
          alias,
          team_id,
          teams (
            id,
            name
          )
        `)
        .order('alias', { ascending: true });
    },
    { operation: 'Get all team aliases' }
  ),

  /**
   * Delete a team alias by ID
   * @param {string} aliasId - The alias ID to delete
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (aliasId) => {
      if (!aliasId) {
        throw new Error('Alias ID is required');
      }

      return await supabase
        .from('team_aliases')
        .delete()
        .eq('id', aliasId);
    },
    { operation: 'Delete team alias' }
  ),

  /**
   * Delete all aliases for a team
   * @param {string} teamId - The team ID
   * @returns {Promise<APIResponse>}
   */
  deleteByTeamId: createAPIMethod(
    async (teamId) => {
      if (!teamId) {
        throw new Error('Team ID is required');
      }

      return await supabase
        .from('team_aliases')
        .delete()
        .eq('team_id', teamId);
    },
    { operation: 'Delete aliases for team' }
  ),

  /**
   * Batch create multiple aliases
   * @param {Array<{alias: string, teamId: string}>} aliases - Array of alias objects
   * @param {string} [createdBy] - User ID of who created the aliases
   * @returns {Promise<APIResponse>}
   */
  batchCreate: createAPIMethod(
    async (aliases, createdBy = null) => {
      if (!aliases || aliases.length === 0) {
        return { data: [], error: null };
      }

      const insertData = aliases
        .filter(a => a.alias && a.teamId)
        .map(a => ({
          alias: a.alias.trim(),
          team_id: a.teamId,
          ...(createdBy ? { created_by: createdBy } : {})
        }));

      if (insertData.length === 0) {
        return { data: [], error: null };
      }

      // Remove duplicates based on alias (case-insensitive)
      const seen = new Set();
      const uniqueData = insertData.filter(item => {
        const lowerAlias = item.alias.toLowerCase();
        if (seen.has(lowerAlias)) {
          return false;
        }
        seen.add(lowerAlias);
        return true;
      });

      log.info(`Creating ${uniqueData.length} team aliases (${insertData.length - uniqueData.length} duplicates removed)`);

      return await supabase
        .from('team_aliases')
        .insert(uniqueData)
        .select(`
          id,
          alias,
          team_id,
          teams (
            id,
            name
          )
        `);
    },
    { operation: 'Batch create team aliases' }
  ),

  /**
   * Build a lookup map of aliases for efficient validation
   * Returns a Map where key is lowercase alias and value is team data
   * @returns {Promise<Map<string, Object>>}
   */
  buildLookupMap: async () => {
    const result = await teamAliasAPI.getAll();

    if (!result.success || !result.data) {
      log.warn('Failed to build alias lookup map:', result.error);
      return new Map();
    }

    const lookupMap = new Map();

    for (const alias of result.data) {
      if (alias.alias && alias.teams) {
        lookupMap.set(alias.alias.toLowerCase().trim(), {
          aliasId: alias.id,
          alias: alias.alias,
          teamId: alias.team_id,
          team: alias.teams
        });
      }
    }

    log.info(`Built alias lookup map with ${lookupMap.size} entries`);
    return lookupMap;
  },

  /**
   * Check if an alias already exists (case-insensitive)
   * @param {string} alias - The alias to check
   * @returns {Promise<boolean>}
   */
  exists: createAPIMethod(
    async (alias) => {
      if (!alias) {
        return { data: false, error: null };
      }

      const { count, error } = await supabase
        .from('team_aliases')
        .select('id', { count: 'exact', head: true })
        .ilike('alias', alias.trim());

      if (error) {
        throw error;
      }

      return { data: count > 0, error: null };
    },
    { operation: 'Check alias exists' }
  )
};

export default teamAliasAPI;
